import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/contexts/AuthContext";
import {
  Drop,
  MapPinSimple,
  CloudSun,
  Clock,
  FloppyDisk,
  ArrowLeft,
  FilePdf,
  FileText,
  Trash,
  PencilSimpleLine,
  Info,
  ClockCounterClockwise,
  PaperPlaneTilt,
  X,
} from "@phosphor-icons/react";

const toLocalIso = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

export default function ApplicationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: a }, { data: fs }, { data: h }] = await Promise.all([
        axios.get(`${API}/app/applications/${id}`),
        axios.get(`${API}/app/fields`),
        axios.get(`${API}/app/applications/${id}/history`).catch(() => ({ data: { entries: [] } })),
      ]);
      setApp(a);
      setFields(fs);
      setHistory(h.entries || []);
      setForm({
        field_id: a.field_id || "",
        product_name: a.product_name || "",
        active_ingredient: a.active_ingredient || "",
        dosage: a.dosage ?? "",
        dosage_unit: a.dosage_unit || "l/ha",
        treated_area_ha: a.treated_area_ha ?? "",
        target_pest: a.target_pest || "",
        waiting_period_days: a.waiting_period_days ?? "",
        weather_temp_c: a.weather_temp_c ?? "",
        weather_conditions: a.weather_conditions || "",
        wind_speed_kmh: a.wind_speed_kmh ?? "",
        verwendungsart: a.verwendungsart || "freiland",
        applied_at: toLocalIso(a.applied_at),
        notes: a.notes || "",
      });
    } catch (err) {
      toast.error(
        err.response?.status === 404
          ? "Aufzeichnung nicht gefunden"
          : "Laden fehlgeschlagen"
      );
      navigate("/app/applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const update = (k, v) => setForm((f) => ({...f, [k]: v }));

  const onSave = async (e) => {
    e.preventDefault();
    if (!form.field_id) {
      toast.error("Bitte eine Fläche wählen");
      return;
    }
    setSaving(true);
    try {
      const payload = {...form,
        dosage: form.dosage === "" ? null : parseFloat(form.dosage),
        treated_area_ha:
          form.treated_area_ha === "" ? null : parseFloat(form.treated_area_ha),
        weather_temp_c:
          form.weather_temp_c === "" ? null : parseFloat(form.weather_temp_c),
        wind_speed_kmh:
          form.wind_speed_kmh === "" ? null : parseFloat(form.wind_speed_kmh),
        waiting_period_days:
          form.waiting_period_days === ""
            ? null
            : parseInt(form.waiting_period_days, 10),
        applied_at: form.applied_at
          ? new Date(form.applied_at).toISOString()
          : app.applied_at,
      };
      const { data } = await axios.patch(
        `${API}/app/applications/${id}`,
        payload
      );
      setApp(data);
      setEditMode(false);
      toast.success("Änderungen gespeichert");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Aufzeichnung wirklich löschen? Nicht umkehrbar.")) return;
    try {
      await axios.delete(`${API}/app/applications/${id}`);
      toast.success("Aufzeichnung gelöscht");
      navigate("/app/applications");
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const exportPdf = async () => {
    try {
      const res = await axios.get(`${API}/app/export?format=pdf&ids=${id}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `plantus-anwendung-${id.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen");
    } catch {
      toast.error("PDF-Export fehlgeschlagen");
    }
  };

  const exportJson = async () => {
    try {
      const { data } = await axios.get(
        `${API}/app/export?format=json&ids=${id}`
      );
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `plantus-anwendung-${id.slice(0, 8)}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("JSON heruntergeladen");
    } catch {
      toast.error("JSON-Export fehlgeschlagen");
    }
  };

  if (loading) {
    return (
      <div className="font-body text-sm text-[#7A827C]" data-testid="app-detail-loading">
        Lade …
      </div>
    );
  }

  if (!app) return null;

  return (
    <div className="max-w-3xl" data-testid="app-detail-page">
      <Link
        to="/app/applications"
        data-testid="app-detail-back"
        className="inline-flex items-center gap-1.5 font-body text-sm text-forest-700 hover:text-forest-900 mb-4"
      >
        <ArrowLeft size={16} weight="bold" /> Zurück zur Übersicht
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1
            className="font-heading text-3xl font-semibold text-forest-900"
            data-testid="app-detail-title"
          >
            {app.product_name}
          </h1>
          <p className="mt-1 font-body text-[15px] text-[#4A524C]">
            {app.field_name} · {new Date(app.applied_at).toLocaleString("de-DE")}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {!editMode && (
            <button
              onClick={() => setEditMode(true)}
              data-testid="app-detail-edit-btn"
              className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white font-medium px-4 py-2.5 rounded-xl text-sm transition"
            >
              <PencilSimpleLine size={16} weight="bold" /> Bearbeiten
            </button>
          )}
          <button
            onClick={exportPdf}
            data-testid="app-detail-export-pdf"
            className="inline-flex items-center gap-2 bg-white border border-bone-200 text-forest-700 hover:border-forest-600/40 px-4 py-2.5 rounded-xl font-medium text-sm transition"
          >
            <FilePdf size={16} weight="duotone" /> PDF
          </button>
          <button
            onClick={exportJson}
            data-testid="app-detail-export-json"
            className="inline-flex items-center gap-2 bg-white border border-bone-200 text-forest-700 hover:border-forest-600/40 px-4 py-2.5 rounded-xl font-medium text-sm transition"
          >
            <FileText size={16} weight="duotone" /> JSON
          </button>
          <button
            onClick={() => setEmailOpen(true)}
            data-testid="app-detail-email-btn"
            className="inline-flex items-center gap-2 bg-white border border-bone-200 text-forest-700 hover:border-forest-600/40 px-4 py-2.5 rounded-xl font-medium text-sm transition"
          >
            <PaperPlaneTilt size={16} weight="duotone" /> Per E-Mail
          </button>
          <button
            onClick={onDelete}
            data-testid="app-detail-delete-btn"
            className="inline-flex items-center gap-2 bg-white border border-bone-200 text-red-600 hover:border-red-500/40 px-4 py-2.5 rounded-xl font-medium text-sm transition"
          >
            <Trash size={16} /> Löschen
          </button>
        </div>
      </div>

      {editMode ? (
        <form
          onSubmit={onSave}
          data-testid="app-detail-form"
          className="bg-white rounded-3xl border border-bone-200 p-6 sm:p-8 space-y-5"
        >
          <Section title="Was und wo" icon={Drop}>
            <Select
              label="Fläche"
              value={form.field_id}
              onChange={(v) => update("field_id", v)}
              testId="edit-field"
              required
            >
              <option value="">, bitte wählen,</option>
              {fields.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                  {f.crop ? ` · ${f.crop}` : ""}
                </option>
              ))}
            </Select>
            <Select
              label="Art der Verwendung (EU 2023/564)"
              value={form.verwendungsart}
              onChange={(v) => update("verwendungsart", v)}
              testId="edit-verwendungsart"
              required
            >
              <option value="freiland">Freiland</option>
              <option value="gewaechshaus">Gewächshaus</option>
              <option value="saatgutbehandlung">Saatgutbehandlung</option>
            </Select>
            <Grid>
              <Input
                label="Pflanzenschutzmittel"
                value={form.product_name}
                onChange={(v) => update("product_name", v)}
                required
                testId="edit-product"
              />
              <Input
                label="Wirkstoff"
                value={form.active_ingredient}
                onChange={(v) => update("active_ingredient", v)}
                testId="edit-active"
              />
            </Grid>
            <Grid cols={3}>
              <Input
                label="Menge"
                type="number"
                step="0.01"
                value={form.dosage}
                onChange={(v) => update("dosage", v)}
                required
                testId="edit-dosage"
              />
              <Select
                label="Einheit"
                value={form.dosage_unit}
                onChange={(v) => update("dosage_unit", v)}
                testId="edit-unit"
              >
                <option value="l/ha">l/ha</option>
                <option value="kg/ha">kg/ha</option>
                <option value="g/ha">g/ha</option>
                <option value="ml/ha">ml/ha</option>
              </Select>
              <Input
                label="Behandelte Fläche (ha)"
                type="number"
                step="0.01"
                value={form.treated_area_ha}
                onChange={(v) => update("treated_area_ha", v)}
                testId="edit-area"
              />
            </Grid>
            <Grid>
              <Input
                label="Schaderreger"
                value={form.target_pest}
                onChange={(v) => update("target_pest", v)}
                testId="edit-pest"
              />
              <Input
                label="Wartezeit (Tage)"
                type="number"
                value={form.waiting_period_days}
                onChange={(v) => update("waiting_period_days", v)}
                testId="edit-wait"
              />
            </Grid>
          </Section>

          <Section title="Zeit & Wetter" icon={CloudSun}>
            <Grid>
              <Input
                label="Zeitpunkt"
                type="datetime-local"
                value={form.applied_at}
                onChange={(v) => update("applied_at", v)}
                required
                testId="edit-time"
              />
              <Input
                label="Temperatur (°C)"
                type="number"
                step="0.1"
                value={form.weather_temp_c}
                onChange={(v) => update("weather_temp_c", v)}
                testId="edit-temp"
              />
            </Grid>
            <Grid>
              <Input
                label="Wetter"
                value={form.weather_conditions}
                onChange={(v) => update("weather_conditions", v)}
                testId="edit-weather"
              />
              <Input
                label="Wind (km/h)"
                type="number"
                step="0.1"
                value={form.wind_speed_kmh}
                onChange={(v) => update("wind_speed_kmh", v)}
                testId="edit-wind"
              />
            </Grid>
          </Section>

          <Section title="Notiz" icon={Clock}>
            <TextArea
              label="Bemerkungen"
              value={form.notes}
              onChange={(v) => update("notes", v)}
              testId="edit-notes"
            />
          </Section>

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              data-testid="app-detail-save-btn"
              className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 disabled:opacity-60 text-white font-heading font-medium px-6 py-3 rounded-xl transition shadow-sm"
            >
              <FloppyDisk size={16} weight="bold" />
              {saving ? "Speichere …" : "Änderungen speichern"}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditMode(false);
                load();
              }}
              data-testid="app-detail-cancel-btn"
              className="inline-flex items-center gap-2 bg-white border border-bone-200 text-[#4A524C] hover:bg-bone-100 font-medium px-6 py-3 rounded-xl text-sm transition"
            >
              Abbrechen
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-5" data-testid="app-detail-view">
          <ViewSection title="Mittel und Menge" icon={Drop}>
            <DataRow label="Produkt" value={app.product_name} />
            <DataRow label="Wirkstoff" value={app.active_ingredient} />
            <DataRow
              label="Menge"
              value={
                app.dosage ? `${app.dosage} ${app.dosage_unit || ""}` : ","
              }
            />
            <DataRow
              label="Behandelte Fläche"
              value={app.treated_area_ha ? `${app.treated_area_ha} ha` : ","}
            />
            <DataRow label="Schaderreger" value={app.target_pest} />
            <DataRow
              label="Wartezeit"
              value={
                app.waiting_period_days ? `${app.waiting_period_days} Tage` : ","
              }
            />
            <DataRow
              label="Verwendungsart"
              value={
                {
                  freiland: "Freiland",
                  gewaechshaus: "Gewächshaus",
                  saatgutbehandlung: "Saatgutbehandlung",
                }[app.verwendungsart] || app.verwendungsart || ","
              }
            />
          </ViewSection>

          <ViewSection title="Wetter" icon={CloudSun}>
            <DataRow
              label="Temperatur"
              value={
                app.weather_temp_c != null ? `${app.weather_temp_c} °C` : ","
              }
            />
            <DataRow label="Bedingungen" value={app.weather_conditions} />
            <DataRow
              label="Wind"
              value={
                app.wind_speed_kmh != null ? `${app.wind_speed_kmh} km/h` : ","
              }
            />
            <DataRow label="Luftfeuchtigkeit" value={app.luftfeuchtigkeit} />
          </ViewSection>

          <ViewSection title="Ort" icon={MapPinSimple}>
            <DataRow
              label="GPS"
              value={
                app.lat != null && app.lng != null
                  ? `${app.lat.toFixed(5)}, ${app.lng.toFixed(5)}`
                  : ","
              }
            />
            <DataRow label="Fläche" value={app.field_name} />
            <DataRow label="Kultur" value={app.kultur} />
          </ViewSection>

          {app.mixture && app.mixture.length > 0 && (
            <ViewSection title="Tankmischung" icon={Info}>
              <ul className="space-y-2">
                {app.mixture.map((m, i) => (
                  <li
                    key={i}
                    className="p-3 rounded-xl bg-bone/60 border border-bone-200"
                  >
                    <div className="font-heading text-forest-900 font-semibold">
                      {m.product_name}
                    </div>
                    <div className="text-sm text-[#4A524C]">
                      {m.dosage} {m.dosage_unit || "l/ha"}
                      {m.active_ingredient ? ` · ${m.active_ingredient}` : ""}
                      {m.target_pest ? ` · ${m.target_pest}` : ""}
                    </div>
                  </li>
                ))}
              </ul>
            </ViewSection>
          )}

          {app.notes && (
            <ViewSection title="Notizen" icon={Clock}>
              <div className="text-sm text-[#4A524C] whitespace-pre-wrap">
                {app.notes}
              </div>
            </ViewSection>
          )}

          <ViewSection title="Erfassung" icon={Info}>
            <DataRow label="Erfasst durch" value={app.user_name} />
            <DataRow
              label="Erfasst am"
              value={
                app.created_at
                  ? new Date(app.created_at).toLocaleString("de-DE")
                  : ","
              }
            />
            {app.updated_at && (
              <DataRow
                label="Zuletzt geändert"
                value={new Date(app.updated_at).toLocaleString("de-DE")}
              />
            )}
          </ViewSection>

          <HistorySection
            entries={history}
            expanded={showHistory}
            onToggle={() => setShowHistory((v) => !v)}
          />
        </div>
      )}

      {emailOpen && (
        <EmailModal
          app={app}
          onClose={() => setEmailOpen(false)}
          onSuccess={() => {
            setEmailOpen(false);
            load();
          }}
        />
      )}
    </div>
  );
}

const HistorySection = ({ entries, expanded, onToggle }) => {
  if (!entries || entries.length === 0) return null;
  return (
    <div className="bg-white rounded-2xl border border-bone-200 p-5" data-testid="app-history">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left"
        data-testid="app-history-toggle"
      >
        <div className="flex items-center gap-2">
          <ClockCounterClockwise size={18} weight="duotone" className="text-forest-700" />
          <h3 className="font-heading text-sm font-semibold uppercase tracking-[0.18em] text-forest-700">
            Änderungsverlauf
          </h3>
          <span className="ml-2 text-xs bg-forest-600/10 text-forest-700 px-2 py-0.5 rounded-full font-semibold">
            {entries.length}
          </span>
        </div>
        <span className="text-xs text-[#7A827C]">
          {expanded ? "Einklappen" : "Anzeigen"}
        </span>
      </button>
      {expanded && (
        <ul className="mt-4 space-y-3 border-l-2 border-bone-200 pl-4">
          {entries.map((e) => (
            <li key={e.id} className="relative">
              <div className="absolute -left-[22px] top-1 w-3 h-3 rounded-full bg-forest-600 border-2 border-white shadow-sm" />
              <div className="text-xs text-[#7A827C]">
                {new Date(e.created_at).toLocaleString("de-DE")}
              </div>
              <div className="text-sm text-forest-900 font-medium mt-0.5">
                <span
                  className={`inline-block px-2 py-0.5 rounded text-xs font-semibold mr-2 uppercase tracking-wider ${
                    e.action === "created"
                      ? "bg-lime-100 text-lime-800"
                      : e.action === "deleted"
                      ? "bg-red-100 text-red-800"
                      : e.action === "emailed"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-amber-100 text-amber-800"
                  }`}
                >
                  {actionLabel(e.action)}
                </span>
                {e.user_name || "Unbekannt"}
              </div>
              {e.changes && Object.keys(e.changes).length > 0 && (
                <div className="mt-1 text-xs text-[#4A524C] space-y-0.5">
                  {formatChanges(e.action, e.changes)}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const actionLabel = (a) =>
  ({
    created: "Erstellt",
    updated: "Geändert",
    deleted: "Gelöscht",
    emailed: "Per E-Mail",
  }[a] || a);

const formatChanges = (action, changes) => {
  if (action === "emailed") {
    return <div>An: {changes.to}</div>;
  }
  if (action === "created" || action === "deleted") {
    return Object.entries(changes).filter(([k]) => k !== "email_id").map(([k, v]) => (
        <div key={k}>
          <span className="text-[#7A827C]">{fieldLabel(k)}:</span>{" "}
          <span className="text-forest-900">{String(v ?? ",")}</span>
        </div>
      ));
  }
  return Object.entries(changes).map(([k, diff]) => (
    <div key={k}>
      <span className="text-[#7A827C]">{fieldLabel(k)}:</span>{" "}
      <span className="text-red-600 line-through">
        {String(diff.from ?? ",").slice(0, 40)}
      </span>{" "}
      <span className="text-forest-700">→</span>{" "}
      <span className="text-forest-900">
        {String(diff.to ?? ",").slice(0, 40)}
      </span>
    </div>
  ));
};

const fieldLabel = (k) =>
  ({
    product_name: "Produkt",
    active_ingredient: "Wirkstoff",
    dosage: "Menge",
    dosage_unit: "Einheit",
    treated_area_ha: "Fläche",
    target_pest: "Schaderreger",
    waiting_period_days: "Wartezeit",
    weather_temp_c: "Temperatur",
    weather_conditions: "Wetter",
    wind_speed_kmh: "Wind",
    applied_at: "Zeitpunkt",
    notes: "Notiz",
    field_id: "Fläche-ID",
    field_name: "Fläche",
    verwendungsart: "Verwendungsart",
    to: "Empfänger",
    subject: "Betreff",
  }[k] || k);

const EmailModal = ({ app, onClose, onSuccess }) => {
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const onSend = async (e) => {
    e.preventDefault();
    if (!to) return;
    setSending(true);
    try {
      await axios.post(`${API}/app/applications/${app.id}/email`, {
        to,
        message,
      });
      toast.success(`PDF an ${to} versendet`);
      onSuccess();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(
        typeof d === "string" ? d : "E-Mail-Versand fehlgeschlagen"
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-forest-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="email-modal-backdrop"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSend}
        className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl"
        data-testid="email-modal"
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-xl font-semibold text-forest-900">
            PDF per E-Mail versenden
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-[#7A827C] hover:text-forest-900 transition"
            aria-label="Schließen"
          >
            <X size={20} />
          </button>
        </div>
        <p className="text-sm text-[#4A524C] mb-5">
          Weiterleitung der Anwendungsdokumentation an Berater, Behörde oder
          Auftraggeber. Als PDF-Anhang im Behördenformat (EU 2023/564).
        </p>
        <div className="space-y-4">
          <div>
            <label className="block font-body text-sm font-medium text-forest-900 mb-1.5">
              Empfänger *
            </label>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              required
              data-testid="email-modal-to"
              placeholder="berater@landwirtschaftskammer.de"
              className="w-full font-body text-[15px] px-3.5 py-2.5 rounded-xl border border-bone-200 bg-bone focus:outline-none focus:border-forest-600 focus:ring-2 focus:ring-forest-600/15"
            />
          </div>
          <div>
            <label className="block font-body text-sm font-medium text-forest-900 mb-1.5">
              Nachricht (optional)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              data-testid="email-modal-message"
              placeholder="Bitte prüfen Sie die anhängende Dokumentation …"
              className="w-full font-body text-[15px] px-3.5 py-2.5 rounded-xl border border-bone-200 bg-bone focus:outline-none focus:border-forest-600 focus:ring-2 focus:ring-forest-600/15 resize-none"
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl bg-white border border-bone-200 text-[#4A524C] hover:bg-bone-100 font-medium text-sm transition"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={sending || !to}
            data-testid="email-modal-send"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-forest-600 hover:bg-forest-700 disabled:opacity-60 text-white font-medium text-sm transition"
          >
            <PaperPlaneTilt size={16} weight="bold" />
            {sending ? "Sende …" : "PDF versenden"}
          </button>
        </div>
      </form>
    </div>
  );
};

const Section = ({ title, icon: Icon, children }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-2 pt-2">
      <Icon size={18} weight="duotone" className="text-forest-700" />
      <h3 className="font-heading text-sm font-semibold uppercase tracking-[0.18em] text-forest-700">
        {title}
      </h3>
    </div>
    {children}
  </div>
);

const ViewSection = ({ title, icon: Icon, children }) => (
  <div className="bg-white rounded-2xl border border-bone-200 p-5">
    <div className="flex items-center gap-2 mb-3">
      <Icon size={18} weight="duotone" className="text-forest-700" />
      <h3 className="font-heading text-sm font-semibold uppercase tracking-[0.18em] text-forest-700">
        {title}
      </h3>
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const DataRow = ({ label, value }) => (
  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
    <div className="font-body text-xs uppercase tracking-wider text-[#7A827C] min-w-[140px]">
      {label}
    </div>
    <div className="font-body text-sm text-forest-900 flex-1">
      {value || <span className="text-[#B8BCB9]">,</span>}
    </div>
  </div>
);

const Grid = ({ children, cols = 2 }) => (
  <div
    className={`grid grid-cols-1 ${cols === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-4`}
  >
    {children}
  </div>
);

const Input = ({ label, testId, onChange,...props }) => (
  <div>
    <label className="block font-body text-sm font-medium text-forest-900 mb-1.5">
      {label}
    </label>
    <input
      data-testid={testId}
      onChange={(e) => onChange?.(e.target.value)}
      className="w-full font-body text-[15px] px-3.5 py-2.5 rounded-xl border border-bone-200 bg-bone focus:outline-none focus:border-forest-600 focus:ring-2 focus:ring-forest-600/15 transition"
      {...props}
    />
  </div>
);

const Select = ({ label, children, testId, onChange,...props }) => (
  <div>
    <label className="block font-body text-sm font-medium text-forest-900 mb-1.5">
      {label}
    </label>
    <select
      data-testid={testId}
      onChange={(e) => onChange?.(e.target.value)}
      className="w-full font-body text-[15px] px-3.5 py-2.5 rounded-xl border border-bone-200 bg-bone focus:outline-none focus:border-forest-600 focus:ring-2 focus:ring-forest-600/15 transition"
      {...props}
    >
      {children}
    </select>
  </div>
);

const TextArea = ({ label, testId, onChange, value,...props }) => (
  <div>
    <label className="block font-body text-sm font-medium text-forest-900 mb-1.5">
      {label}
    </label>
    <textarea
      data-testid={testId}
      onChange={(e) => onChange?.(e.target.value)}
      value={value || ""}
      rows={3}
      className="w-full font-body text-[15px] px-3.5 py-2.5 rounded-xl border border-bone-200 bg-bone focus:outline-none focus:border-forest-600 focus:ring-2 focus:ring-forest-600/15 transition resize-none"
      {...props}
    />
  </div>
);
